import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Settings, Trash2 } from "lucide-react";
import { RevealLine } from "@/components/RevealLine";
import { LegalCrossLinks } from "@/pages/Terms";

const ON_CONFIRM = [
  "your account will be deactivated;",
  "your account will enter a 30-day recovery period; and",
  "during that period your information will be retained only to allow account recovery and to comply with applicable legal obligations.",
];

const AFTER_RECOVERY = [
  "your account will be permanently deleted;",
  "personal information held directly by Offcut will be securely deleted or permanently deidentified where reasonably practicable; and",
  "information processed by third-party service providers on our behalf will be deleted or deidentified in accordance with our instructions, applicable legal obligations and those providers' retention processes.",
];

const RETAINED_FOR = [
  "taxation and accounting obligations;",
  "fraud prevention;",
  "security investigations;",
  "legal compliance;",
  "dispute resolution; and",
  "establishing, exercising or defending legal claims.",
];

const Block = ({ title, children, delay = 0 }) => (
  <motion.section
    initial={{ opacity: 0, y: 28 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.6, delay, ease: [0.76, 0, 0.24, 1] }}
    className="border-t border-white/10 pt-8"
  >
    {title && (
      <h2 className="font-brand text-3xl tracking-wide text-white sm:text-4xl">
        {title}
      </h2>
    )}
    <div className="mt-4 space-y-3 text-sm leading-relaxed text-white/70 sm:text-base">
      {children}
    </div>
  </motion.section>
);

const Bullets = ({ items }) => (
  <ul className="list-disc space-y-2 pl-5">
    {items.map((item) => (
      <li key={item}>{item}</li>
    ))}
  </ul>
);

export default function AccountDeletion() {
  return (
    <motion.main
      data-testid="account-deletion-page"
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.7, ease: [0.76, 0, 0.24, 1] }}
      className="relative z-10 mx-auto max-w-3xl px-6 pb-32 pt-32 sm:px-10"
    >
      <Link
        to="/"
        data-testid="page-logo-home-link"
        aria-label="Back to home"
        className="block w-fit rounded-lg transition-transform duration-300 hover:scale-105 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/50"
      >
        <img
          src="/chippymate-logo.png"
          alt="Offcut — back to home"
          className="h-auto w-28"
        />
      </Link>
      <p className="mt-2 text-xs font-semibold uppercase tracking-[0.35em] text-white/50">
        Offcut - ChippyMate
      </p>
      <h1 className="font-brand mt-3 text-6xl tracking-wide text-white sm:text-7xl">
        <RevealLine>Account Deletion</RevealLine>
      </h1>
      <p className="mt-6 text-xs font-semibold uppercase tracking-[0.2em] text-white/50">
        Last updated: 2026
      </p>

      <div className="mt-14 space-y-12">
        <Block title="How To Delete Your Account">
          <p>
            You may request deletion of your Offcut account at any time within
            the application by navigating to:
          </p>
          <p className="flex flex-wrap items-center gap-2 rounded-xl border border-white/15 bg-black/30 px-5 py-4 font-semibold text-white backdrop-blur-md">
            <Settings className="h-4 w-4 text-white/60" />
            Settings
            <span className="text-white/40">&rarr;</span>
            <Trash2 className="h-4 w-4 text-white/60" />
            Delete Account
          </p>
          <p>
            Before processing a deletion request, we may require reasonable
            verification of your identity to protect your personal information.
          </p>
        </Block>

        <Block title="Once Your Request Is Confirmed">
          <Bullets items={ON_CONFIRM} />
          <p>
            Changed your mind? Simply sign back in within the 30-day period to
            reactivate your account — everything will be right where you left
            it.
          </p>
        </Block>

        <Block title="After The 30-Day Recovery Period">
          <p>If your account is not reactivated within the recovery period:</p>
          <Bullets items={AFTER_RECOVERY} />
        </Block>

        <Block title="Information We May Keep">
          <p>
            Certain information may continue to be retained where required or
            authorised by law, including information reasonably necessary for:
          </p>
          <Bullets items={RETAINED_FOR} />
        </Block>

        <Block title="Subscriptions">
          <p>
            Deleting your Offcut account does not automatically cancel
            subscriptions purchased through Apple, Google Play or another
            subscription provider. Subscriptions must be cancelled separately
            through the platform from which they were purchased.
          </p>
        </Block>
      </div>

      <motion.p
        initial={{ opacity: 0, y: 28 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-60px" }}
        transition={{ duration: 0.6, ease: [0.76, 0, 0.24, 1] }}
        className="mt-16 border-t border-white/10 pt-8 text-sm leading-relaxed text-white/70 sm:text-base"
      >
        If you have any questions about account deletion, please reach out via
        the{" "}
        <Link
          to="/contact"
          className="font-semibold text-white underline decoration-white/40 underline-offset-4 transition-colors duration-300 hover:decoration-white"
        >
          Contact Us
        </Link>{" "}
        page.
      </motion.p>

      <LegalCrossLinks current="/account-deletion" />
    </motion.main>
  );
}
